export const STARTUP_MILESTONES = [
  'signed-in-shell',
  'map-object',
  'selected-base-source-tile',
  'first-base-image',
  'location-decision',
  'stable-view',
] as const;

export type StartupMilestone = (typeof STARTUP_MILESTONES)[number];

export interface StartupMilestoneEvent {
  name: StartupMilestone;
  at: number;
  detail?: Record<string, unknown>;
}

interface StartupCoordinatorOptions {
  now?: () => number;
  markPerformance?: (name: string) => void;
}

export interface StartupCoordinator {
  mark(name: StartupMilestone, detail?: Record<string, unknown>): StartupMilestoneEvent;
  has(name: StartupMilestone): boolean;
  waitFor(name: StartupMilestone): Promise<StartupMilestoneEvent>;
  snapshot(): StartupMilestoneEvent[];
}

function defaultPerformanceMark(name: string): void {
  try {
    globalThis.performance?.mark(name);
  } catch {
    // Performance marks are evidence only. Startup must not depend on them.
  }
}

/** One small source of truth for the opening milestones measured by the audit. */
export function createStartupCoordinator(
  options: StartupCoordinatorOptions = {},
): StartupCoordinator {
  const now = options.now ?? (() => globalThis.performance?.now?.() ?? Date.now());
  const markPerformance = options.markPerformance ?? defaultPerformanceMark;
  const events = new Map<StartupMilestone, StartupMilestoneEvent>();
  const waiters = new Map<StartupMilestone, Array<(event: StartupMilestoneEvent) => void>>();

  const mark = (
    name: StartupMilestone,
    detail?: Record<string, unknown>,
  ): StartupMilestoneEvent => {
    const existing = events.get(name);
    if (existing) return existing;

    const event: StartupMilestoneEvent = { name, at: now(), ...(detail ? { detail } : {}) };
    events.set(name, event);
    markPerformance(`pps:${name}`);
    for (const resolve of waiters.get(name) ?? []) resolve(event);
    waiters.delete(name);
    return event;
  };

  return {
    mark,
    has: (name) => events.has(name),
    waitFor: (name) => {
      const existing = events.get(name);
      if (existing) return Promise.resolve(existing);
      return new Promise<StartupMilestoneEvent>((resolve) => {
        const current = waiters.get(name) ?? [];
        current.push(resolve);
        waiters.set(name, current);
      });
    },
    snapshot: () => Array.from(events.values()),
  };
}

export const startupCoordinator = createStartupCoordinator();
